/**
 * Release 8 item 11: removing the endpoint agent only with approval, and the
 * tenant's emergency maintenance code (api-controller edge/agent-removal.ts).
 *
 *   ask for a removal, cancel one                         edge:remove:write
 *   approve or reject a removal; ask/approve/reject/cancel a code change
 *                                                         edge:remove:approve (never your own ask)
 *   list requests, the code's status                      foundational:read
 *
 * The maintenance code is made by TWO people (second review F2): a maker asks,
 * a different approver approves, and only the approve answer carries the code.
 */
import { apiFetch, newIdempotencyKey } from './api';

export const REMOVE_WRITE = 'edge:remove:write';
export const REMOVE_APPROVE = 'edge:remove:approve';

export type RemovalState = 'awaiting_approval' | 'approved' | 'rejected' | 'cancelled' | 'expired';
export type DeviceRemovalProgress =
  | 'not_sent'
  | 'waiting_for_device'
  | 'started_on_device'
  | 'refused_by_device'
  | 'device_never_collected';

export interface RemovalRequest {
  id: string;
  podRowId: string;
  podId: string;
  displayName: string | null;
  agentVersion: string | null;
  state: RemovalState;
  reason: string;
  requestedBy: string;
  requestedAt: string;
  expiresAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  progress: DeviceRemovalProgress;
  deviceDetail: string | null;
  deviceAckedAt: string | null;
  lastCheckInAt: string | null;
}

export interface MaintenanceCodeStatus {
  configured: boolean;
  codeId: string | null;
  algorithm: string | null;
  iterations: number | null;
  createdBy: string | null;
  createdAt: string | null;
  reason: string | null;
  retiredCount: number;
  /** Live laptop agents the caller can see, and how many report holding the current code. */
  laptopsTotal: number;
  laptopsOnCurrentCode: number;
  /** A make-or-rotate ask waiting for a second person, if any. */
  pendingRequest: MaintenanceCodeRequest | null;
}

export interface MaintenanceCodeRequest {
  id: string;
  action: 'create' | 'rotate';
  state: RemovalState;
  reason: string;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  newCodeId: string | null;
  expiresAt: string;
}

export interface NewMaintenanceCode {
  code: string;
  codeId: string;
  createdAt: string;
  previousCodeId: string | null;
  action: 'create' | 'rotate';
}

const post = <T>(path: string, body: Record<string, unknown>): Promise<T> =>
  apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body), idempotencyKey: newIdempotencyKey() });

export const requestAgentRemoval = (podRowId: string, reason: string): Promise<{ request: RemovalRequest }> =>
  post(`/v1/edge/fleet/${encodeURIComponent(podRowId)}/removal-requests`, { reason });

export function listRemovalRequests(filter: { state?: RemovalState; podRowId?: string } = {}): Promise<{ requests: RemovalRequest[] }> {
  const q = new URLSearchParams();
  if (filter.state) q.set('state', filter.state);
  if (filter.podRowId) q.set('podRowId', filter.podRowId);
  const s = q.toString();
  return apiFetch(`/v1/edge/removal-requests${s ? `?${s}` : ''}`);
}

const decide = (id: string, act: 'approve' | 'reject' | 'cancel', note?: string): Promise<{ request: RemovalRequest }> =>
  post(`/v1/edge/removal-requests/${encodeURIComponent(id)}/${act}`, note ? { note } : {});

export const approveRemoval = (id: string, note?: string) => decide(id, 'approve', note);
export const rejectRemoval = (id: string, note?: string) => decide(id, 'reject', note);
/** A request still waiting, or an approved one the device has not collected yet. */
export const cancelRemoval = (id: string) => decide(id, 'cancel');

export const getMaintenanceCodeStatus = (): Promise<MaintenanceCodeStatus> => apiFetch('/v1/edge/maintenance-code');

export const listMaintenanceCodeRequests = (): Promise<{ requests: MaintenanceCodeRequest[] }> =>
  apiFetch('/v1/edge/maintenance-code/requests');

/** A maker asks for the code to be made or rotated; a different person approves. */
export const requestMaintenanceCodeChange = (reason: string): Promise<{ request: MaintenanceCodeRequest }> =>
  post('/v1/edge/maintenance-code/requests', { reason });

const decideCode = (id: string, act: 'approve' | 'reject' | 'cancel', note?: string) =>
  post(`/v1/edge/maintenance-code/requests/${encodeURIComponent(id)}/${act}`, note ? { note } : {});

/** The approver's call. The code is in this answer only: show it once, never keep it. */
export const approveMaintenanceCodeChange = (id: string, note?: string): Promise<NewMaintenanceCode> =>
  decideCode(id, 'approve', note) as Promise<NewMaintenanceCode>;
export const rejectMaintenanceCodeChange = (id: string, note?: string): Promise<{ request: MaintenanceCodeRequest }> =>
  decideCode(id, 'reject', note) as Promise<{ request: MaintenanceCodeRequest }>;
export const cancelMaintenanceCodeChange = (id: string): Promise<{ request: MaintenanceCodeRequest }> =>
  decideCode(id, 'cancel') as Promise<{ request: MaintenanceCodeRequest }>;

/** Plain words for where an approved removal is on the device. */
export function progressWords(p: DeviceRemovalProgress): string {
  switch (p) {
    case 'not_sent':
      return 'not sent to the device';
    case 'waiting_for_device':
      return 'waiting for the device to check in';
    case 'started_on_device':
      return 'removal started on the device';
    case 'refused_by_device':
      return 'the device could not remove itself';
    case 'device_never_collected':
      return 'the device did not check in before the approval expired';
  }
}

/** Why the current person may not approve: they asked for it (four eyes). */
export function ownRequest(r: Pick<RemovalRequest, 'requestedBy'>, actorId: string | null): boolean {
  return !!actorId && r.requestedBy.toLowerCase() === actorId.toLowerCase();
}
